<template>  
  <section ref="sectionRef" class="heroproduct min-h-screen pt-0 lg:pt-22"
    :class="['index-' + index + ' bg-gradient-' + gradient + ' slide-align-' + align + ' text-' + textcolor + 
      ' introslide flex flex-col justify-center  bg-workbook-dark-grey bg-cover bg-center'            
    ]"
    
    :style="image ? { backgroundImage: `url(${bgImageUrl})` } : {}">
    
    <UContainer :class="isPrint ? 'h-[2000px]' : ''" class="relative lg:min-h-[720px] flex items-center w-full">
      <WorkbookIcon v-if="icons" v-for="(icon, index) in iconsArray" class="absolute top-12 w-24 h-24 origin-center"
            :name="'workbook:' + icon" :key="icon + props.textcolor" :style="'color: ' + textcolor" />
      <UBadge v-if="callout" :label="callout"  variant="solid" class="callout uppercase absolute top-12 right-0 origin-center" />    
      <div class="w-full flex gap-4 flex-col lg:flex-row items-center" :class="{ 'lg:flex-row-reverse': align === 'right' }">
        <div class="w-full lg:w-5/12"> 
          <slot />
        </div>
        <div class="hero-image w-full lg:w-7/12 flex items-center gap-4 justify-between"
          :class="{ 'justify-start': align === 'right', 'lg:justify-end': align === 'left' }">
          <TwicImg
            v-if="productimage"            
            class="w-full p-15"
            refit
            mode="contain"
          :src="productimage + '?deployment=' + config.public.appVersion"
            auto="format,compress"
            ratio="1217/1513"            
            eager="true"
            sizes="(min-width: 1000px) 50vw, 96vw"
            placeholder="none"
          />
        </div>
      </div>
    </UContainer>
  </section>
  
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed } from 'vue'

import { useIntroStore } from '@/store/intro'
import { useWindowSize } from '@vueuse/core'
import { useRuntimeConfig } from '#app'
import { toTwicpicBgUrl } from '@/utils/productImages'
import { loadGsap } from '@/utils/gsap'
import { useRoute } from 'vue-router'



const route = useRoute()
const config = useRuntimeConfig()

const introStore = useIntroStore()
const { width } = useWindowSize()

const sectionRef = ref<HTMLElement | null>(null)
const isMobile = computed(() => width.value < 767)

const index = ref(99);

const props = defineProps({
  align: {
    type: String,
    default: 'left'
  },
  textcolor: {
    type: String,
    default: 'white'
  },
  image: {
    type: String,
    default: ''
  },
  pdfimage: {
    type: String,
    default: ''
  },
  productimage: {
    type: String    
  },
  icons: {
    type: String
  },
  callout: {
    type: String
  },
  snap: {
    type: String,
    default: 'true'
  },
  gradient: {
    type: String,
    default: '' // Alternativ: light-blue
  },
  style: {
    type: Number,    
  }
})

const bgImageUrl = computed(() => {
  if (isPrint.value) {
    return props.pdfimage ? toTwicpicBgUrl(props.pdfimage + '?deployment=' + config.public.appVersion , true) : (props.image ? toTwicpicBgUrl(props.image + '?deployment=' + config.public.appVersion, true) : undefined)
  }
  return props.image ? toTwicpicBgUrl(props.image + '?deployment=' + config.public.appVersion, false) : undefined
})
const iconsArray = computed(() => props.icons ? props.icons.split(',').map(i => i.trim()) : [])


const isPrint = computed(() =>
  route.name?.toString().toLowerCase().includes('print') ||
  route.path.toLowerCase().includes('print')  
)

let ctx: gsap.Context | null = null

onMounted(async () => {
  index.value = introStore.mountedSlides.length
  if (sectionRef.value) {
    introStore.registerSlide(sectionRef.value)
  }
  if (isMobile.value || isPrint.value) return

  if (import.meta.client) {
    const { gsap } = await loadGsap()

    // Animate the header
    ctx = gsap.context(() => {
      const h1 = sectionRef.value?.querySelector('h1')
      const h2 = sectionRef.value?.querySelector('h2')
      const pElements = sectionRef.value?.querySelectorAll('p') || []
      const iconElements = sectionRef.value?.querySelectorAll('.workbook-icon') || []
      const calloutElements = sectionRef.value?.querySelectorAll('.callout') || []
      const heroImage = sectionRef.value?.querySelector('.hero-image')

      const scrollStart = computed(() => (index.value === 0 ? "top -10%" : "top 30%"))
      const scrollEnd = computed(() => "bottom 50%")
      const xStart = computed(() => (props.align === 'left' ? -100 : 100))
      const xEnd = computed(() => (props.align === 'left' ? 100 : -100))

    
      let tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.value,
          pin: index.value === 99,
          markers: false,
          start: scrollStart.value,
          end: scrollEnd.value,
          scrub: true,
          snap: props.snap === 'true' ? {
            snapTo: 'labels',
            duration: { min: 0.2, max: 3 },
            delay: 0.2,
          } : false
        }
      })
      
      // ANIMATION FOR FIRST SLIDE
      if (index.value === 0) {
        if (h1) {
          tl.to(h1, { x: 100, duration: 10 })
          tl.to(h1, { opacity: 0, duration: 2 }, "-=8")
        }
        if (h2) {
          tl.to(h2, { x: 150, duration: 8 }, "-=8")
          tl.to(h2, { opacity: 0, duration: 2 }, "-=2")
        }
        if (pElements.length) {
          tl.to(pElements, { y: 200, duration: 6 }, "-=5")
          tl.to(pElements, { opacity: 0, duration: 2, stagger: 1 }, "-=2")
        }
      }
      else if (index.value > 0) {
        // ANIMATION FOR OTHER SLIDES
        if (h1) {
          tl.from(h1, { x: xStart.value, duration: 10 })
          tl.from(h1, { opacity: 0, duration: 3 }, "-=10")
        }
        if (h2) {
          tl.from(h2, { x: xStart.value, duration: 10 }, "-=8")
          tl.from(h2, { opacity: 0, duration: 3 }, "-=10")
        }
        if (pElements.length) {
          tl.from(pElements, { x: xStart.value, duration: 10 }, "-=8")
          tl.from(pElements, { opacity: 0, duration: 6, stagger: 1 }, "-=10")
        }
        if (iconElements.length) {
          tl.from(iconElements, { scale: 1.5, opacity: 0, duration: 3, stagger: 1.5 }, "-=10")
        }
        if (calloutElements.length) {
          tl.from(
            calloutElements,
            { scale: 0, opacity: 0, duration: 5, stagger: 1.5, ease: "elastic.out(1, 0.5)" },
            "-=2"
          )
        }
        if (heroImage) {
          tl.from(heroImage, { scale: .75, opacity: 0, duration: 8 }, "-=12")
        }

        tl.addLabel("midpoint")
        if (h1) {
          tl.fromTo(h1, { x: 0 }, { x: xEnd.value, duration: 10 }, "+=12")
          tl.to(h1, { opacity: 0, duration: 2 }, "-=6")
        }
        if (h2) {
          tl.to(h2, { x: xEnd.value, duration: 10 }, "-=7")
          tl.to(h2, { opacity: 0, duration: 2 }, "-=6")
        }
        if (pElements.length) {
          tl.to(pElements, { y: 200, duration: 10 }, "-=7")
          tl.to(pElements, { opacity: 0, duration: 4, stagger: -1 }, "-=6")
        }
        if (iconElements.length) {
          tl.to(iconElements, { x: xEnd.value, opacity: 0, duration: 4, stagger: 1 }, "-=10")
        }
      }
    }, sectionRef.value || undefined)
  }
})

onUnmounted(() => {
  ctx?.revert()
  ctx = null
})
</script>

<style scoped>
@reference "../../assets/css/main.css";
:deep(h1) { @apply text-base lg:text-3xl font-bold mb-4 }
:deep(h2) { @apply text-3xl lg:text-5xl font-bold mb-12 }
:deep(p)  { @apply text-base lg:text-lg  }
.slide-align-right :deep(h1) { @apply self-end }
.slide-align-right :deep(h2) { @apply self-end }
.slide-align-right :deep(p)  { @apply self-end }
</style>
